import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Sparkles,
  Loader2,
  CheckCircle2,
  X,
  ArrowRight,
  BookOpen,
  AlertCircle,
  LayoutTemplate,
  Beaker,
  Calculator,
  Clock,
  BookMarked,
  PenTool,
  Zap,
  ClipboardList,
  Brain
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject icons mapping
const subjectIcons = {
  science: Beaker,
  math: Calculator,
  history: Clock,
  english: PenTool
};

// Preset icons mapping
const presetIcons = {
  zap: Zap,
  clipboard: ClipboardList,
  'book-open': BookOpen,
  brain: Brain
};

const CreateQuizPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Tab State
  const [activeTab, setActiveTab] = useState('custom');
  
  // Templates State
  const [templates, setTemplates] = useState({});
  const [presets, setPresets] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('science');
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Lesson State
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [passageText, setPassageText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Quiz Settings
  const [numQuestions, setNumQuestions] = useState([10]);
  const [questionTypes, setQuestionTypes] = useState(['multiple_choice']);
  const [difficulty, setDifficulty] = useState('medium');
  const [showPassageToStudents, setShowPassageToStudents] = useState(false);

  // Math Mode State
  const [quizMode, setQuizMode] = useState('reading'); // 'reading' | 'math'
  const [mathCatalog, setMathCatalog] = useState({ topics: [], grade_bands: [], question_types: [] });
  const [mathTopics, setMathTopics] = useState(['addition', 'subtraction']);
  const [mathQuestionTypes, setMathQuestionTypes] = useState(['numeric_fill_in_blank', 'multiple_choice']);
  const [mathProgress, setMathProgress] = useState(null); // {current: n, total: n, label: str}

  // Generation State
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [lessonId, setLessonId] = useState(null);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`, {
          headers: getAuthHeader()
        });
        setTemplates(response.data.templates);
        setPresets(response.data.presets);
        setMyTemplates(response.data.my_templates || []);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [getAuthHeader]);

  // Fetch math catalog once
  useEffect(() => {
    const fetchMathCatalog = async () => {
      try {
        const res = await axios.get(`${API}/math/catalog`, { headers: getAuthHeader() });
        setMathCatalog(res.data);
      } catch (error) {
        console.error('Failed to load math catalog:', error);
      }
    };
    fetchMathCatalog();
  }, [getAuthHeader]);

  const toggleMathTopic = (topicId) => {
    setMathTopics((prev) =>
      prev.includes(topicId) ? prev.filter((t) => t !== topicId) : [...prev, topicId]
    );
  };

  const toggleMathQuestionType = (typeId) => {
    setMathQuestionTypes((prev) =>
      prev.includes(typeId) ? prev.filter((t) => t !== typeId) : [...prev, typeId]
    );
  };

  const MATH_BATCH_SIZE = 5;

  const handleGenerateMathQuiz = async () => {
    if (mathTopics.length === 0) {
      toast.error('Please select at least one math topic');
      return;
    }
    if (mathQuestionTypes.length === 0) {
      toast.error('Please select at least one question type');
      return;
    }
    if (!gradeLevel) {
      toast.error('Please select a grade level');
      return;
    }
    const gradeBand = gradeToMathBand(gradeLevel);
    setIsGenerating(true);

    const total = numQuestions[0];
    const batchCount = Math.ceil(total / MATH_BATCH_SIZE);
    const allQuestions = [];

    try {
      for (let b = 0; b < batchCount; b++) {
        const batchNum = b + 1;
        const remaining = total - allQuestions.length;
        const batchSize = Math.min(MATH_BATCH_SIZE, remaining);
        const startNum = allQuestions.length + 1;
        const endNum = allQuestions.length + batchSize;

        setMathProgress({
          current: batchNum,
          total: batchCount,
          label: `Generating questions ${startNum}–${endNum}${batchCount > 1 ? ` (batch ${batchNum} of ${batchCount})` : ''}`,
        });

        const payload = {
          grade_band: gradeBand,
          topics: mathTopics,
          num_questions: batchSize,
          question_types: mathQuestionTypes,
          difficulty,
          previous_questions: allQuestions.map((q) => q.question),
        };

        // Try once, retry once on failure. Preserve everything already generated.
        let batchQuestions = null;
        let lastErr = null;
        for (let attempt = 1; attempt <= 2 && !batchQuestions; attempt++) {
          try {
            const res = await axios.post(
              `${API}/quizzes/generate-math-batch`,
              payload,
              { headers: getAuthHeader(), timeout: 90000 }
            );
            batchQuestions = res.data.questions || [];
          } catch (err) {
            lastErr = err;
            if (attempt === 1) {
              // brief pause before retry
              await new Promise((r) => setTimeout(r, 1200));
              toast.warning(`Batch ${batchNum} failed — retrying...`);
            }
          }
        }

        if (!batchQuestions || batchQuestions.length === 0) {
          // Retry exhausted. Preserve what we have.
          console.error('Math batch failed', lastErr);
          toast.error(
            `Batch ${batchNum} failed after retry. Kept the ${allQuestions.length} questions we already have.`
          );
          break;
        }

        allQuestions.push(...batchQuestions);
      }

      if (allQuestions.length === 0) {
        setMathProgress(null);
        toast.error('Could not generate any math questions. Please try again.');
        return;
      }

      // Finalize: create the placeholder Mathematics lesson
      setMathProgress({
        current: batchCount,
        total: batchCount,
        label: 'Finalizing quiz...',
      });
      const finalizeRes = await axios.post(
        `${API}/quizzes/finalize-math`,
        {
          title: title || null,
          grade_band: gradeBand,
          topics: mathTopics,
        },
        { headers: getAuthHeader(), timeout: 20000 }
      );

      setLessonId(finalizeRes.data.lesson_id);
      setGeneratedQuestions({
        lesson_id: finalizeRes.data.lesson_id,
        lesson_title: finalizeRes.data.lesson_title,
        subject: finalizeRes.data.subject,
        grade_level: finalizeRes.data.grade_level,
        is_math: true,
        questions: allQuestions,
      });

      if (allQuestions.length < total) {
        toast.warning(
          `Generated ${allQuestions.length} of ${total} questions. You can regenerate to try again.`
        );
      } else {
        toast.success(`Math quiz generated with ${allQuestions.length} questions!`);
      }
    } catch (error) {
      console.error('Math generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate math quiz');
    } finally {
      setIsGenerating(false);
      setMathProgress(null);
    }
  };

  // Apply template to form
  const applyTemplate = (template) => {
    setTitle(template.name);
    setSubject(template.subject);
    setGradeLevel(template.grade_level);
    setPassageText(template.passage_text);
    setNumQuestions([template.config.num_questions]);
    setQuestionTypes(template.config.question_types);
    setDifficulty(template.config.difficulty);
    setActiveTab('custom');
    setLessonId(null);
    setGeneratedQuestions(null);
    toast.success(`Template "${template.name}" loaded!`);
  };

  // Delete a custom template
  const deleteMyTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/templates/my/${templateId}`, {
        headers: getAuthHeader()
      });
      setMyTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Apply preset configuration only
  const applyPreset = (preset) => {
    setNumQuestions([preset.config.num_questions]);
    setQuestionTypes(preset.config.question_types);
    setDifficulty(preset.config.difficulty);
    toast.success(`"${preset.name}" settings applied!`);
  };

  const questionTypeOptions = [
    { id: 'multiple_choice', label: 'Multiple Choice' },
    { id: 'true_false', label: 'True/False' },
    { id: 'short_answer', label: 'Short Answer' },
    { id: 'fill_in_blank', label: 'Fill in the Blank' }
  ];

  const subjects = [
    'English Language Arts',
    'Mathematics',
    'Science',
    'Social Studies',
    'History',
    'Geography',
    'Biology',
    'Chemistry',
    'Physics',
    'Literature',
    'Other'
  ];

  const gradeLevels = [
    'Kindergarten',
    '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
    '6th Grade', '7th Grade', '8th Grade',
    '9th Grade', '10th Grade', '11th Grade', '12th Grade',
    'College/Adult'
  ];

  // Map an individual grade label → math grade_band accepted by the backend
  const gradeToMathBand = (label) => {
    if (!label) return 'grades_3_5';
    const l = label.toLowerCase();
    if (l.includes('kindergarten')) return 'kindergarten';
    if (l.startsWith('1st') || l.startsWith('2nd')) return 'grades_1_2';
    if (l.startsWith('3rd') || l.startsWith('4th') || l.startsWith('5th')) return 'grades_3_5';
    if (l.startsWith('6th') || l.startsWith('7th') || l.startsWith('8th')) return 'grades_6_8';
    return 'high_school';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['.pdf', '.docx', '.txt'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    setUploadedFile(file);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name.replace(/\.[^/.]+$/, ''));
      formData.append('subject', subject || 'General');
      formData.append('grade_level', gradeLevel || 'Not specified');

      const response = await axios.post(`${API}/lessons/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setLessonId(response.data.id);
      setPassageText(response.data.passage_text);
      if (!title) setTitle(response.data.title);
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateLesson = async () => {
    if (!passageText.trim()) {
      toast.error('Please enter lesson content');
      return null;
    }

    try {
      const response = await axios.post(`${API}/lessons`, {
        title: title || 'Untitled Lesson',
        subject: subject || 'General',
        grade_level: gradeLevel || 'Not specified',
        passage_text: passageText
      }, {
        headers: getAuthHeader()
      });

      setLessonId(response.data.id);
      return response.data.id;
    } catch (error) {
      console.error('Create lesson error:', error);
      toast.error('Failed to create lesson');
      return null;
    }
  };

  const handleGenerateQuiz = async () => {
    if (!passageText.trim()) {
      toast.error('Please enter or upload lesson content');
      return;
    }

    if (questionTypes.length === 0) {
      toast.error('Please select at least one question type');
      return;
    }

    setIsGenerating(true);

    try {
      let currentLessonId = lessonId;
      if (!currentLessonId) {
        currentLessonId = await handleCreateLesson();
        if (!currentLessonId) {
          setIsGenerating(false);
          return;
        }
      }

      const response = await axios.post(`${API}/quizzes/generate`, {
        lesson_id: currentLessonId,
        num_questions: numQuestions[0],
        question_types: questionTypes,
        difficulty: difficulty
      }, {
        headers: getAuthHeader()
      });

      setGeneratedQuestions(response.data);
      toast.success('Quiz generated successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate quiz');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveQuiz = async () => {
    if (!generatedQuestions) return;

    try {
      const response = await axios.post(`${API}/quizzes`, {
        title: title || 'Untitled Quiz',
        lesson_id: lessonId,
        questions: generatedQuestions.questions,
        show_passage_to_students: showPassageToStudents
      }, {
        headers: getAuthHeader()
      });

      toast.success('Quiz saved!');
      navigate(`/quiz/${response.data.id}`);
    } catch (error) {
      console.error('Save error:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Quiz limit reached. Buy more or upgrade your plan.', {
          action: {
            label: 'Get More',
            onClick: () => navigate('/dashboard')
          }
        });
      } else {
        toast.error('Failed to save quiz');
      }
    }
  };

  const toggleQuestionType = (typeId) => {
    setQuestionTypes(prev => 
      prev.includes(typeId)
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const removeFile = () => {
    setUploadedFile(null);
    setPassageText('');
    setLessonId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
            Create New Quiz
          </h1>
          <p className="text-muted-foreground text-lg">
            Start from a template or create your own custom quiz
          </p>
        </motion.div>

        {/* Tabs for Templates vs Custom */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="templates" className="gap-2" data-testid="templates-tab">
              <LayoutTemplate className="w-4 h-4" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="custom" className="gap-2" data-testid="custom-tab">
              <PenTool className="w-4 h-4" />
              Custom
            </TabsTrigger>
          </TabsList>

          {/* Templates Tab Content */}
          <TabsContent value="templates" className="mt-6">
            <div className="space-y-8">
              {/* My Templates (User's Custom Templates) */}
              {myTemplates.length > 0 && (
                <div>
                  <h3 className="font-serif text-lg font-medium mb-4 flex items-center gap-2">
                    <BookMarked className="w-5 h-5 text-accent" />
                    My Templates
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">Your saved custom templates</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myTemplates.map((template) => (
                      <Card
                        key={template.id}
                        className="cursor-pointer hover:border-accent/50 transition-all hover:shadow-md group border-accent/20 bg-accent/5"
                        onClick={() => applyTemplate(template)}
                        data-testid={`my-template-${template.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <Badge className="bg-accent/20 text-accent-foreground text-xs">Custom</Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10 hover:text-destructive"
                              onClick={(e) => deleteMyTemplate(template.id, e)}
                              data-testid={`delete-my-template-${template.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <h4 className="font-serif font-medium text-lg mb-2 group-hover:text-accent transition-colors">
                            {template.name}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {template.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <span>{template.subject}</span>
                            <span>•</span>
                            <span>{template.grade_level}</span>
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{template.config.num_questions} Q</Badge>
                            <Badge variant="outline" className="text-xs capitalize">{template.config.difficulty}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Presets */}
              <div>
                <h3 className="font-serif text-lg font-medium mb-4">Quick Presets</h3>
                <p className="text-sm text-muted-foreground mb-4">Apply these settings to any content you provide</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {presets.map((preset) => {
                    const IconComponent = presetIcons[preset.icon] || ClipboardList;
                    return (
                      <Card
                        key={preset.id}
                        className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-md"
                        onClick={() => applyPreset(preset)}
                        data-testid={`preset-${preset.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-medium text-sm">{preset.name}</h4>
                              <p className="text-xs text-muted-foreground mt-1">{preset.description}</p>
                              <div className="flex gap-1 mt-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs">{preset.config.num_questions} Q</Badge>
                                <Badge variant="outline" className="text-xs capitalize">{preset.config.difficulty}</Badge>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Subject Templates */}
              <div>
                <h3 className="font-serif text-lg font-medium mb-4">Subject Templates</h3>
                <p className="text-sm text-muted-foreground mb-4">Ready-to-use lesson content with optimized settings</p>
                
                {/* Subject Filter */}
                <div className="flex gap-2 mb-6 flex-wrap">
                  {Object.keys(templates).map((subjectKey) => {
                    const IconComponent = subjectIcons[subjectKey] || BookMarked;
                    return (
                      <Button
                        key={subjectKey}
                        variant={selectedSubject === subjectKey ? 'default' : 'outline'}
                        size="sm"
                        className="gap-2 capitalize"
                        onClick={() => setSelectedSubject(subjectKey)}
                        data-testid={`subject-filter-${subjectKey}`}
                      >
                        <IconComponent className="w-4 h-4" />
                        {subjectKey}
                      </Button>
                    );
                  })}
                </div>

                {/* Template Cards */}
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(templates[selectedSubject] || []).map((template) => (
                      <Card
                        key={template.id}
                        className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-md group"
                        onClick={() => applyTemplate(template)}
                        data-testid={`template-${template.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <Badge variant="secondary" className="text-xs">{template.subject}</Badge>
                            <Badge variant="outline" className="text-xs">{template.grade_level}</Badge>
                          </div>
                          <h4 className="font-serif font-medium text-lg mb-2 group-hover:text-primary transition-colors">
                            {template.name}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            {template.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{template.config.num_questions} questions</span>
                            <span>•</span>
                            <span className="capitalize">{template.config.difficulty}</span>
                          </div>
                          <div className="flex gap-1 mt-3 flex-wrap">
                            {template.config.question_types.slice(0, 3).map((type) => (
                              <Badge key={type} variant="outline" className="text-xs capitalize">
                                {type.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                          <div className="mt-4 pt-4 border-t border-border">
                            <Button size="sm" className="w-full gap-2 group-hover:bg-primary">
                              Use Template
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Custom Tab Content */}
          <TabsContent value="custom" className="mt-6">
            {/* Quiz Mode Toggle */}
            <div className="mb-6 flex items-center gap-2 p-1 bg-secondary/50 rounded-xl w-fit">
              <button
                type="button"
                onClick={() => setQuizMode('reading')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quizMode === 'reading' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="mode-reading-btn"
              >
                Reading Quiz
              </button>
              <button
                type="button"
                onClick={() => setQuizMode('math')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quizMode === 'math' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="mode-math-btn"
              >
                Math Quiz
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Lesson Input */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <BookOpen className="w-5 h-5" />
                      {quizMode === 'math' ? 'Math Quiz Setup' : 'Lesson Content'}
                    </CardTitle>
                    <CardDescription>
                      {quizMode === 'math'
                        ? 'Pick a grade level and math topics — no passage needed'
                        : 'Paste your lesson text or upload a document'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Quiz Title</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Chapter 5: The American Revolution"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="h-12"
                    data-testid="quiz-title-input"
                  />
                </div>

                {/* Subject & Grade */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select value={subject} onValueChange={setSubject}>
                      <SelectTrigger className="h-12" data-testid="subject-select">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Grade Level</Label>
                    <Select value={gradeLevel} onValueChange={setGradeLevel}>
                      <SelectTrigger className="h-12" data-testid="grade-select">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {gradeLevels.map((g) => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* File Upload — Reading mode only */}
                {quizMode === 'reading' && (
                <div className="space-y-2">
                  <Label>Upload Document (Optional)</Label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                      uploadedFile ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                  >
                    {uploadedFile ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-sm truncate max-w-[200px]">{uploadedFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {isUploading ? 'Processing...' : 'Uploaded'}
                            </p>
                          </div>
                        </div>
                        {isUploading ? (
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={removeFile}
                            className="hover:bg-destructive/10"
                            data-testid="remove-file-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm font-medium text-foreground mb-1">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground">
                          PDF, DOCX, or TXT
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".pdf,.docx,.txt"
                          onChange={handleFileUpload}
                          data-testid="file-upload-input"
                        />
                      </label>
                    )}
                  </div>
                </div>
                )}

                {/* Passage Text — Reading mode only */}
                {quizMode === 'reading' && (
                <div className="space-y-2">
                  <Label htmlFor="passage">
                    Lesson Text {uploadedFile && '(extracted from file)'}
                  </Label>
                  <Textarea
                    id="passage"
                    placeholder="Paste your lesson content here, or upload a document above..."
                    value={passageText}
                    onChange={(e) => setPassageText(e.target.value)}
                    className="min-h-[300px] resize-y"
                    data-testid="passage-textarea"
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {passageText.split(/\s+/).filter(Boolean).length} words
                  </p>
                </div>
                )}

                {/* Math Setup — Math mode only */}
                {quizMode === 'math' && (
                <>
                  <div className="space-y-3">
                    <Label>Math Topics ({mathTopics.length} selected)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {mathCatalog.topics.map((t) => (
                        <div
                          key={t.id}
                          className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            mathTopics.includes(t.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                          }`}
                          onClick={() => toggleMathTopic(t.id)}
                          data-testid={`math-topic-${t.id}`}
                        >
                          <Checkbox checked={mathTopics.includes(t.id)} onCheckedChange={() => toggleMathTopic(t.id)} />
                          <span className="text-sm font-medium">{t.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-secondary/40 text-xs text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Math notation preview</p>
                    <p>Questions use KaTeX for fractions, exponents, roots, and equations — examples: <code>$\frac{'{3}'}{'{4}'} + \frac{'{1}'}{'{4}'}$</code>, <code>$x^2 + 5x + 6$</code>, <code>$\sqrt{'{64}'}$</code></p>
                  </div>
                </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column - Quiz Settings */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Quiz Settings
                </CardTitle>
                <CardDescription>
                  Configure how your quiz should be generated
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Number of Questions */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Number of Questions</Label>
                    <span className="text-2xl font-serif font-medium text-primary" data-testid="question-count">
                      {numQuestions[0]}
                    </span>
                  </div>
                  <Slider
                    value={numQuestions}
                    onValueChange={setNumQuestions}
                    min={5}
                    max={25}
                    step={1}
                    className="w-full"
                    data-testid="question-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>5</span>
                    <span>25</span>
                  </div>
                </div>

                {/* Question Types */}
                <div className="space-y-3">
                  <Label>Question Types</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {quizMode === 'math'
                      ? mathCatalog.question_types.map((type) => (
                          <div
                            key={type.id}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              mathQuestionTypes.includes(type.id)
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/40'
                            }`}
                            onClick={() => toggleMathQuestionType(type.id)}
                            data-testid={`math-qtype-${type.id}`}
                          >
                            <Checkbox
                              checked={mathQuestionTypes.includes(type.id)}
                              onCheckedChange={() => toggleMathQuestionType(type.id)}
                            />
                            <span className="text-sm font-medium">{type.label}</span>
                          </div>
                        ))
                      : questionTypeOptions.map((type) => (
                          <div
                            key={type.id}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              questionTypes.includes(type.id)
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/40'
                            }`}
                            onClick={() => toggleQuestionType(type.id)}
                            data-testid={`question-type-${type.id}`}
                          >
                            <Checkbox
                              checked={questionTypes.includes(type.id)}
                              onCheckedChange={() => toggleQuestionType(type.id)}
                            />
                            <span className="text-sm font-medium">{type.label}</span>
                          </div>
                        ))}
                  </div>
                </div>

                {/* Difficulty */}
                <div className="space-y-3">
                  <Label>Difficulty Level</Label>
                  <div className="flex gap-3">
                    {['easy', 'medium', 'hard'].map((level) => (
                      <Button
                        key={level}
                        variant={difficulty === level ? 'default' : 'outline'}
                        className={`flex-1 capitalize ${
                          difficulty === level ? 'bg-primary' : ''
                        }`}
                        onClick={() => setDifficulty(level)}
                        data-testid={`difficulty-${level}`}
                      >
                        {level}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Show Passage to Students — Reading mode only */}
                {quizMode === 'reading' && (
                <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-secondary/30">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium cursor-pointer" htmlFor="show-passage">
                      Show Reading Passage to Students
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Students can view the passage before and during the quiz
                    </p>
                  </div>
                  <Checkbox
                    id="show-passage"
                    checked={showPassageToStudents}
                    onCheckedChange={setShowPassageToStudents}
                    data-testid="show-passage-toggle"
                  />
                </div>
                )}

                {/* Generate Button */}
                <Button
                  className="w-full h-14 text-base bg-accent hover:bg-accent/90 gap-2"
                  onClick={quizMode === 'math' ? handleGenerateMathQuiz : handleGenerateQuiz}
                  disabled={
                    isGenerating ||
                    (quizMode === 'reading' && !passageText.trim()) ||
                    (quizMode === 'math' && (mathTopics.length === 0 || mathQuestionTypes.length === 0))
                  }
                  data-testid="generate-quiz-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {quizMode === 'math' && mathProgress
                        ? mathProgress.label
                        : 'Generating Quiz...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate {quizMode === 'math' ? 'Math ' : ''}Quiz
                    </>
                  )}
                </Button>
                {isGenerating && quizMode === 'math' && mathProgress && mathProgress.total > 1 && (
                  <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-2 transition-all"
                      style={{ width: `${(mathProgress.current / mathProgress.total) * 100}%` }}
                      data-testid="math-progress-bar"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generated Questions Preview */}
            <AnimatePresence>
              {generatedQuestions && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Card className="border-primary/30 bg-primary/5">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-primary">Quiz Generated!</p>
                          <p className="text-sm text-muted-foreground">
                            {generatedQuestions.questions.length} questions created
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Button
                          className="w-full gap-2"
                          onClick={handleSaveQuiz}
                          data-testid="save-review-btn"
                        >
                          Save & Review Quiz
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={quizMode === 'math' ? handleGenerateMathQuiz : handleGenerateQuiz}
                          data-testid="regenerate-all-btn"
                        >
                          Regenerate All Questions
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tips */}
            <Card className="border-border bg-secondary/30">
              <CardContent className="p-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-2">Tips for better quizzes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Use clear, well-structured lesson content</li>
                      <li>Include specific facts and details</li>
                      <li>500-1200 words works best per passage</li>
                      <li>Mix question types for comprehensive assessment</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CreateQuizPage;
